import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getMyEnrollments, getMyPayments } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { GraduationCap, CreditCard, Calendar } from 'lucide-react';

export default function StudentMyCourses() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getMyEnrollments();
        setEnrollments(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="my-courses-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">
            Mis Cursos
          </h1>
          <p className="text-[#737373] mt-2">Gestiona tus inscripciones</p>
        </div>

        {enrollments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enrollments.map((enrollment) => (
              <Card key={enrollment.id} data-testid={`my-course-${enrollment.course_id}`} className="bg-white border border-[#E5DFD3] shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-[#1A1A1A]">{enrollment.course_title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 text-sm">
                    <CreditCard className="h-4 w-4 text-[#8B1538]" />
                    <span className="text-[#4A4A4A] capitalize">{enrollment.subscription_type}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-[#8B1538]" />
                    <span className="text-[#4A4A4A]">Expira: {new Date(enrollment.expires_at).toLocaleDateString('es-ES')}</span>
                  </div>
                  <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#2D6A4F]/10 text-[#2D6A4F]">
                    Activo
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <GraduationCap className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" />
            <h3 className="font-heading text-xl text-[#1A1A1A] mb-2">No tienes cursos inscritos</h3>
            <p className="text-[#737373]">Explora nuestros cursos disponibles</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

// Student Payments Page
export function StudentPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getMyPayments();
        setPayments(data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="my-payments-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">
            Mis Pagos
          </h1>
          <p className="text-[#737373] mt-2">Historial de transacciones</p>
        </div>

        {payments.length > 0 ? (
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E5DFD3]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Fecha</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Monto</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Tipo</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#737373]">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} data-testid={`payment-${payment.id}`} className="border-b border-[#E5DFD3] table-row-hover">
                        <td className="py-3 px-4 text-sm text-[#1A1A1A]">
                          {new Date(payment.created_at).toLocaleDateString('es-ES')}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-[#8B1538]">
                          ${payment.amount.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-[#4A4A4A] capitalize">
                          {payment.subscription_type}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            payment.status === 'completed' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#B8860B]/10 text-[#B8860B]'
                          }`}>
                            {payment.status === 'completed' ? 'Completado' : 'Pendiente'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-16">
            <CreditCard className="h-16 w-16 mx-auto text-[#D9B48F] mb-4" />
            <h3 className="font-heading text-xl text-[#1A1A1A] mb-2">No hay pagos registrados</h3>
            <p className="text-[#737373]">Tus transacciones aparecerán aquí</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
